import { CalibrationDisplaySchema } from "../types";

/**
 * Strain Gauge calibration display schema.
 *
 * Strain gauge calibrations are submitted via API by an external calibration
 * tool after a 4-channel, 9-point mV sweep. There is no manual input form —
 * inputFieldConfig is intentionally omitted.
 *
 * This schema only configures how submitted data is displayed (inputData /
 * outputData) and how the certificate form looks.
 */
export const StrainGaugeSchema: CalibrationDisplaySchema = {
  sensorType: "Dynamic",
  unit: "mV",

  // No inputFieldConfig — calibration cannot be started from the frontend.

  certificateFormConfig: {
    title: "Resistive Strain Gauge Certificate Details",
    fields: [
      {
        key: "date",
        label: "Date",
        type: "date",
        placeholder: "DD.MM.YYYY",
      },
      {
        key: "temperature",
        label: "Temperature",
        type: "text",
        placeholder: "e.g., 26 °C",
        unit: "°C",
      },
      {
        key: "humidity",
        label: "Humidity",
        type: "text",
        placeholder: "e.g., 55 %",
        unit: "%",
      },
    ],
  },

  inputData: {
    sections: [
      {
        title: "Test Configuration",
        bgColor: "bg-blue-50",
        group: "setup",
        fields: [
          { key: "full_scale_range", label: "Full Scale Range", unit: "" },
          { key: "temperature", label: "Temperature", unit: "°C" },
          { key: "humidity", label: "Humidity", unit: "%" },
          { key: "firmware_version", label: "Firmware Version", unit: "" },
          { key: "device_model", label: "Device Model", unit: "" },
        ],
      },
      {
        title: "Calibration Coefficients",
        bgColor: "bg-indigo-50",
        group: "setup",
        fields: [
          { key: "calibration_coefficients.offset_ch0", label: "Offset Ch0", unit: "mV" },
          { key: "calibration_coefficients.offset_ch1", label: "Offset Ch1", unit: "mV" },
          { key: "calibration_coefficients.offset_ch2", label: "Offset Ch2", unit: "mV" },
          { key: "calibration_coefficients.offset_ch3", label: "Offset Ch3", unit: "mV" },
          { key: "calibration_coefficients.gauge_factor_ch0", label: "Gauge Factor Ch0", unit: "" },
          { key: "calibration_coefficients.gauge_factor_ch1", label: "Gauge Factor Ch1", unit: "" },
          { key: "calibration_coefficients.gauge_factor_ch2", label: "Gauge Factor Ch2", unit: "" },
          { key: "calibration_coefficients.gauge_factor_ch3", label: "Gauge Factor Ch3", unit: "" },
        ],
        condition: (data: unknown) =>
          typeof data === "object" &&
          data !== null &&
          "calibration_coefficients" in data,
      },
    ],

    tables: [
      {
        title: "9-Point Channel Calibration Sweep",
        bgColor: "bg-green-50",
        group: "measurement",
        dataKey: "test_report.test_results",
        groupByKey: "channel",
        groupTitle: (channel: unknown) => `Channel ${channel}`,
        columns: [
          { key: "channel", label: "Channel", unit: "" },
          { key: "applied_mv", label: "Applied (mV)", unit: "", className: "font-mono" },
          { key: "measured_mv", label: "Measured (mV)", unit: "", className: "font-mono" },
          { key: "error_mv", label: "Error (mV)", unit: "", className: "font-mono" },
          {
            key: "full_scale_error_percent",
            label: "Full-scale Error (%)",
            unit: "%",
            className: "font-mono",
          },
          { key: "pass_fail", label: "P/F", unit: "" },
        ],
        rowClassName: (row: unknown) =>
          typeof row === "object" &&
          row !== null &&
          "pass_fail" in row &&
          (row as any).pass_fail === "FAIL"
            ? "bg-red-50"
            : "",
        // No `condition` here — dataKey resolution above already hides the
        // table when there's no data.
      },
    ],
  },

  outputData: {
    sections: [
      {
        title: "Summary",
        bgColor: "bg-green-50",
        group: "result",
        fields: [
          { key: "calibration_status", label: "Calibration Status", unit: "" },
          { key: "full_scale_range", label: "Full Scale Range", unit: "" },
          {
            key: "max_error_percent_fs",
            label: "Max Error %FS",
            unit: "%",
          },
          { key: "tolerance_percent", label: "Tolerance", unit: "%" },
          { key: "temperature", label: "Temperature", unit: "°C" },
          { key: "humidity", label: "Humidity", unit: "%" },
        ],
      },
      {
        title: "Calibration Coefficients",
        bgColor: "bg-indigo-50",
        group: "result",
        fields: [
          { key: "calibration_coefficients.offset_ch0", label: "Offset Ch0", unit: "mV" },
          { key: "calibration_coefficients.offset_ch1", label: "Offset Ch1", unit: "mV" },
          { key: "calibration_coefficients.offset_ch2", label: "Offset Ch2", unit: "mV" },
          { key: "calibration_coefficients.offset_ch3", label: "Offset Ch3", unit: "mV" },
          { key: "calibration_coefficients.gauge_factor_ch0", label: "Gauge Factor Ch0", unit: "" },
          { key: "calibration_coefficients.gauge_factor_ch1", label: "Gauge Factor Ch1", unit: "" },
          { key: "calibration_coefficients.gauge_factor_ch2", label: "Gauge Factor Ch2", unit: "" },
          { key: "calibration_coefficients.gauge_factor_ch3", label: "Gauge Factor Ch3", unit: "" },
        ],
        condition: (data: unknown) =>
          typeof data === "object" &&
          data !== null &&
          "calibration_coefficients" in data,
      },
    ],

    tables: [
      {
        // Computed/verified test results attached to the calibration result
        // record — the channel-by-channel error and pass/fail verdict that
        // backs the calibration's Analysis view.
        title: "9-Point Channel Test Results",
        bgColor: "bg-red-50",
        group: "analysis",
        dataKey: "test_results",
        groupByKey: "channel",
        groupTitle: (channel: unknown) => `Channel ${channel}`,
        columns: [
          { key: "channel", label: "Channel", unit: "" },
          {
            key: "applied_mv",
            label: "Applied (mV)",
            unit: "",
            className: "font-medium",
          },
          {
            key: "measured_mv",
            label: "Measured (mV)",
            unit: "",
            className: "font-mono",
          },
          {
            key: "error_mv",
            label: "Error (mV)",
            unit: "",
            className: "font-mono",
          },
          {
            key: "full_scale_error_percent",
            label: "Full-scale Error (%)",
            unit: "%",
            className: "font-mono",
          },
          { key: "pass_fail", label: "P/F", unit: "" },
        ],
        rowClassName: (row: unknown) =>
          typeof row === "object" &&
          row !== null &&
          "pass_fail" in row &&
          (row as any).pass_fail === "FAIL"
            ? "bg-red-50"
            : "",
        // No `condition` here — dataKey resolution above already hides the
        // table when there's no data.
      },
    ],
  },
};
